/**
 * Raises the shared error modal for a react-query read whose SDK envelope
 * carries an error. A thrown read is already raised by `QueryCache.onError`
 * in `apps/app/src/queryClient.ts`, so this only handles the envelope.
 */
import { useEffect } from "react";

import {
  extractErrorCode,
  forgetQueryApiError,
  showQueryApiError,
} from "../../errors/apiErrorBus";

export type typeRaisedReadError = {
  /** The envelope's error code, or null when the read has no error envelope. */
  errorCode: string | null;
  /** Drops the modal's first-seen stamp for this read, so a failure after a
   *  fan-initiated Try again raises the modal again. Call it right before
   *  `refetch()`. A no-op when `errorCode` is null. */
  forgetError: () => void;
};

export function useRaiseReadError(input: {
  /** The query's `data` (the SDK envelope), or undefined. */
  data: unknown;
  /** The query's `dataUpdatedAt`. */
  dataUpdatedAt: number;
  /** A `queryApiErrorIdentity(...)` string unique to this read on this screen. */
  identity: string;
}): typeRaisedReadError {
  const { data, dataUpdatedAt, identity } = input;
  const errorCode = extractErrorCode(data);

  useEffect(() => {
    showQueryApiError(data, identity, errorCode, dataUpdatedAt);
  }, [data, errorCode, dataUpdatedAt, identity]);

  const forgetError = (): void => {
    if (errorCode !== null) {
      forgetQueryApiError(identity, errorCode);
    }
  };

  return { errorCode, forgetError };
}
